import { useState } from 'react';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  Briefcase,
  Shield,
  AlertTriangle,
  Edit,
  Save,
  X,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';

export const EmployeeProfile = () => {
  const { employee, api, refreshProfile } = useEmployeeAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    phone: employee.phone || '',
    address: employee.address || '',
    city: employee.city || '',
    state: employee.state || '',
    zip_code: employee.zip_code || '',
    emergency_contact: employee.emergency_contact || {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    }
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/api/employee-portal/update-profile', formData);
      toast.success('Profile updated');
      await refreshProfile();
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">My Profile</h1>
          <p className="text-gray-500 text-sm">Your personal information</p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#C6A87C] text-white text-sm rounded-lg"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#C6A87C] text-white text-sm rounded-lg disabled:opacity-50"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] text-white rounded-2xl p-6"
      >
        <div className="flex items-center gap-4">
          <div className="relative">
            {employee.profile_picture_url ? (
              <img 
                src={employee.profile_picture_url.startsWith('/') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${employee.profile_picture_url}` 
                  : employee.profile_picture_url
                }
                alt={employee.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-[#C6A87C]"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#C6A87C] flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold">{employee.name}</h2>
            <p className="text-[#C6A87C]">{employee.role}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-1 bg-white/10 text-white text-xs rounded-full">
                {employee.expertise_level || 'Journeyman'}
              </span>
              <span className="text-white/60 text-xs">{employee.years_experience || 0} yrs exp</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Contact Information */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-[#C6A87C]" />
          <h3 className="font-semibold">Contact Information</h3>
        </div>
        
        <div className="space-y-4">
          {employee.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium">{employee.email}</p>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Phone</p>
              {editing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
                />
              ) : (
                <p className="font-medium">{employee.phone || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Address */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#C6A87C]" />
          <h3 className="font-semibold">Address</h3>
        </div>
        
        {editing ? (
          <div className="space-y-3">
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Street Address"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
            />
            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="City"
                className="px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
              />
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="State"
                className="px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
              />
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                placeholder="ZIP"
                className="px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>
        ) : (
          <div>
            {employee.address || employee.city ? (
              <>
                <p className="font-medium">{employee.address}</p>
                <p className="text-gray-600">
                  {[employee.city, employee.state, employee.zip_code].filter(Boolean).join(', ')}
                </p>
              </>
            ) : (
              <p className="text-gray-500">No address provided</p>
            )}
          </div>
        )}
      </motion.div>

      {/* Emergency Contact */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold">Emergency Contact</h3>
        </div>
        
        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={formData.emergency_contact?.name || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  emergency_contact: { ...formData.emergency_contact, name: e.target.value }
                })}
                placeholder="Contact Name"
                className="px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
              />
              <input
                type="text"
                value={formData.emergency_contact?.relationship || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  emergency_contact: { ...formData.emergency_contact, relationship: e.target.value }
                })}
                placeholder="Relationship"
                className="px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <input
              type="tel"
              value={formData.emergency_contact?.phone || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                emergency_contact: { ...formData.emergency_contact, phone: e.target.value }
              })}
              placeholder="Phone Number"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
            />
          </div>
        ) : employee.emergency_contact?.name ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-medium">{employee.emergency_contact.name}</p>
              <span className="text-sm text-gray-500">{employee.emergency_contact.relationship}</span>
            </div>
            <p className="text-gray-600">{employee.emergency_contact.phone}</p>
          </div>
        ) : (
          <p className="text-gray-500">No emergency contact provided</p>
        )}
      </motion.div>

      {/* Work Information */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-[#C6A87C]" />
          <h3 className="font-semibold">Work Information</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Employee ID</span>
            <span className="font-mono text-sm">{employee.id}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Role</span>
            <span className="font-medium">{employee.role}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Experience Level</span>
            <span className="capitalize">{employee.expertise_level || 'Journeyman'}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-500">Years of Experience</span>
            <span>{employee.years_experience || 0} years</span>
          </div>
        </div>
      </motion.div>

      {/* Certifications */}
      {employee.certifications && employee.certifications.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[#C6A87C]" />
            <h3 className="font-semibold">Certifications</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {employee.certifications.map((cert, i) => (
              <span
                key={i}
                className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full"
              >
                {cert}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Expertise */}
      {employee.expertise && employee.expertise.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-[#C6A87C]" />
            <h3 className="font-semibold">Expertise</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {employee.expertise.map((skill, i) => (
              <span
                key={i}
                className="px-3 py-1 bg-[#C6A87C]/10 text-[#C6A87C] text-sm rounded-full"
              >
                {skill}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EmployeeProfile;
